import { useState, useEffect, useCallback } from 'react';
import { SkuApiService, ClientWithImpactTypes } from '../../../services/skuApi';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import {
	setSelectedClient as setSelectedClientRedux,
	setShowCombineSku,
} from '../../../store/slices/skuMappingSlice';
import { canFilterByCity } from '../../../utils/cityFilterPermissions';

/**
 * Powers the client picker on the Add / Edit SKU mapping pages.
 *
 * @param clientId  When set, hook runs in edit mode (client locked from URL).
 * @param cityIdOverride  Optional explicit city for cross-city users. When
 *   provided, that city's clients are fetched; when null/undefined the
 *   backend's "all cities" branch fires (for founders / type-4) or the
 *   user's own city (for everyone else).
 */
export const useClientSkuMapping = (
	clientId?: string,
	cityIdOverride?: number | null
) => {
	const { user } = useSelector((state: RootState) => state.auth);
	const canCrossCity = canFilterByCity(user?.userTypeId);
	// Cross-city users: honour the page's selected city (or null = all).
	// Everyone else: locked to their own city (backend ignores the param
	// for them anyway, but we send it for clarity in network logs).
	const location_id: number | null = canCrossCity
		? (cityIdOverride ?? null)
		: (user?.city_id ?? null);
	const dispatch = useDispatch<AppDispatch>();

	const isEditMode = !!clientId;

	// Get client info from Redux (persisted)
	const reduxClientId = useSelector((state: RootState) => state.skuMapping.selectedClientId);
	const reduxClient = useSelector((state: RootState) => state.skuMapping.selectedClient);

	const [clients, setClients] = useState<ClientWithImpactTypes[]>([]);
	// In add mode, always start with null (fresh start)
	// In edit mode, initialize from Redux if available
	const [selectedClientId, setSelectedClientId] = useState<number | null>(
		isEditMode ? reduxClientId : null
	);
	const [selectedClient, setSelectedClient] = useState<ClientWithImpactTypes | null>(
		isEditMode ? reduxClient : null
	);
	const [isClientLocked, setIsClientLocked] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string>('');

	useEffect(() => {
		// Don't restore selected client from Redux Persist in add mode
		// Add page should always start fresh - client selection is cleared on navigation
		// Only restore in edit mode where client is required
		if (isEditMode && reduxClientId && reduxClient) {
			setSelectedClientId(reduxClientId);
			setSelectedClient(reduxClient);
		}

		loadClients();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location_id, isEditMode, reduxClientId, reduxClient]);

	useEffect(() => {
		if (isEditMode && clientId && clients.length > 0) {
			const client = clients.find(c => c.clientId.toString() === clientId);
			if (client) {
				setSelectedClient(client);
				setSelectedClientId(client.clientId);
				setIsClientLocked(true);
			}
		} else if (!isEditMode) {
			// In add mode, don't restore client - Add page should always start fresh
			// Client must be explicitly selected by user
			setIsClientLocked(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientId, isEditMode, clients.length]);

	const loadClients = async () => {
		// City-scoped users without a city_id (edge case) skip the fetch.
		// Cross-city users with a null cityId still call — backend returns
		// the all-cities response in that case.
		if (!canCrossCity && !location_id) return;
		try {
			setLoading(true);
			// In add mode, pass clientMap=true; in edit mode, don't pass it
			const response = await SkuApiService.getClientByCity(location_id, !isEditMode);
			if (response.status_code === 200 && response.result) {
				setClients(response.result);
			}
		} catch (error) {
			console.error('Failed to load clients:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleClientChange = useCallback(
		async (clientIdStr: string, onClientSelect: (client: ClientWithImpactTypes) => void) => {
			const numClientId = clientIdStr ? Number(clientIdStr) : null;
			if (!numClientId) {
				setSelectedClientId(null);
				setSelectedClient(null);
				return;
			}

			const client = clients.find(c => c.clientId === numClientId);
			if (!client) return;

			try {
				const response = await SkuApiService.getClientSkuMap(numClientId);
				if (response.status_code === 200) {
					// Extract combineSkuInfo from response
					const combineSkuInfo = response.combineSkuInfo || response.data?.combineSkuInfo;
					const showCombineSku = combineSkuInfo?.showCombineSku === true;
					
					// Store showCombineSku in Redux
					dispatch(setShowCombineSku(showCombineSku));

					if (response.result && response.result.length > 0) {
						setError(`${client.clientName} already has existing mappings.`);
						return;
					}
				}

				// Update both local state AND Redux
				setSelectedClient(client);
				setSelectedClientId(numClientId);
				dispatch(setSelectedClientRedux({ client, clientId: numClientId }));
				onClientSelect(client);
			} catch (error) {
				console.error('Failed to check client mappings:', error);
			}
		},
		[clients, dispatch]
	);

	return {
		clients,
		selectedClientId,
		setSelectedClientId,
		selectedClient,
		setSelectedClient,
		isClientLocked,
		setIsClientLocked,
		loading,
		error,
		setError,
		handleClientChange,
		location_id,
	};
};
